'use client'

import { useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { toast } from 'react-toastify'
import {
    MdCheckCircle,
    MdErrorOutline,
    MdLink,
    MdLinkOff,
    MdQrCode2,
    MdRefresh,
    MdVerified,
} from 'react-icons/md'
import ConfirmModal from '@/components/confirm-modal'
import { SocketContext } from '@/lib/providers/socket-provider'
import {
    connectWhatsappSession,
    getWhatsappSessionStatus,
    logoutWhatsappSession,
    WhatsappChannelStatus,
    WhatsappSessionStatus,
} from '@/lib/api/atendimento/whatsapp-sessions'
import {
    getDepartmentPhoneNumberStatus,
    linkDepartmentPhoneNumber,
    type DepartmentPhoneNumberStatus,
} from '@/lib/api/messaging/whatsapp-account-api'

export type WhatsappProviderKind = 'WHATSAPP_API' | 'WHATSAPP_WEB'

interface Props {
    departmentId: number | null
    provider: WhatsappProviderKind
    onProviderChange: (provider: WhatsappProviderKind) => void
    /** Troca de canal em andamento (gravação imediata no setor). */
    savingChannel?: boolean
}

const STATUS_LABELS: Record<WhatsappSessionStatus, string> = {
    DISCONNECTED: 'Desconectado',
    PENDING_QR: 'Aguardando leitura do QR code',
    CONNECTING: 'Conectando...',
    CONNECTED: 'Conectado',
    LOGGED_OUT: 'Desconectado pelo WhatsApp',
}

/**
 * Cor por estado, aplicada ao ponto e ao texto do cabeçalho de conexão.
 * Substitui o `badge` anterior: badge colorido ao lado de botões coloridos
 * virava mais uma mancha de cor sem hierarquia.
 */
const STATUS_TONES: Record<
    WhatsappSessionStatus,
    { dot: string; text: string; pulse: boolean }
> = {
    DISCONNECTED: {
        dot: 'bg-base-content/30',
        text: 'text-base-content/60',
        pulse: false,
    },
    PENDING_QR: { dot: 'bg-warning', text: 'text-warning', pulse: true },
    CONNECTING: { dot: 'bg-info', text: 'text-info', pulse: true },
    CONNECTED: { dot: 'bg-success', text: 'text-success', pulse: false },
    LOGGED_OUT: { dot: 'bg-error', text: 'text-error', pulse: false },
}

const CHANNELS: {
    kind: WhatsappProviderKind
    title: string
    tagline: string
    description: string
    icon: ReactNode
}[] = [
    {
        kind: 'WHATSAPP_API',
        title: 'WhatsApp API',
        tagline: 'Oficial',
        description:
            'Número registrado na conta oficial do WhatsApp Business. Não exige celular conectado.',
        icon: <MdVerified className="text-xl" />,
    },
    {
        kind: 'WHATSAPP_WEB',
        title: 'WhatsApp Web',
        tagline: 'QR code',
        description:
            'Pareia o celular do setor lendo um QR code. O número é o do aparelho conectado.',
        icon: <MdQrCode2 className="text-xl" />,
    },
]

/**
 * Canal de WhatsApp do setor.
 *
 * No canal da API oficial o número é digitado. No WhatsApp Web ele vem do pareamento por
 * QR code: o backend abre a sessão no worker e transmite QR e status por
 * WebSocket, com polling como rede de segurança (o QR expira em ~1 min).
 */
export default function WhatsappChannelSection({
    departmentId,
    provider,
    onProviderChange,
    savingChannel = false,
}: Props) {
    const { socket } = useContext(SocketContext)
    const [status, setStatus] = useState<WhatsappChannelStatus | null>(null)
    const [qrCode, setQrCode] = useState<string | null>(null)
    const [loading, setCarregando] = useState(false)
    const [confirmingDisconnect, setConfirmandoSaida] = useState(false)
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
    // Momento do clique em Conectar, para detectar espera sem resposta.
    const [requestedAt, setRequestedAt] = useState<number | null>(null)
    const [noResponse, setNoResponse] = useState(false)

    const isWeb = provider === 'WHATSAPP_WEB'
    const [apiLink, setApiLink] = useState<DepartmentPhoneNumberStatus | null>(null)
    const [linking, setLinking] = useState(false)

    // API oficial: o setor só envia e recebe com o `phone_number_id` da Meta gravado.
    useEffect(() => {
        if (isWeb || !departmentId || savingChannel) {
            setApiLink(null)
            return
        }
        getDepartmentPhoneNumberStatus(departmentId).then(res => res.success && setApiLink(res.result))
    }, [isWeb, departmentId, savingChannel])

    const handleLink = async () => {
        if (!departmentId) return
        setLinking(true)
        try {
            const res = await linkDepartmentPhoneNumber(departmentId)
            if (!res.success) {
                toast.error(res.error || 'Não foi possível vincular o número')
                return
            }
            setApiLink(res.result)
            const messages: Record<string, string> = {
                LINKED: 'Número vinculado à conta oficial do WhatsApp.',
                NO_NUMBER: 'Informe o número do WhatsApp do setor e salve antes de vincular.',
                NO_ACCOUNT: 'A organização ainda não tem conta oficial do WhatsApp conectada.',
                NOT_FOUND: res.result.availableNumbers.length
                    ? `Número não encontrado na conta. Números disponíveis: ${res.result.availableNumbers.join(', ')}.`
                    : 'Número não encontrado na conta oficial do WhatsApp.',
                NOT_API: 'O setor não está no canal da API oficial.',
            }
            if (res.result.outcome === 'LINKED') toast.success(messages.LINKED)
            else toast.error(messages[res.result.outcome])
        } catch {
            toast.error('Não foi possível vincular o número')
        } finally {
            setLinking(false)
        }
    }
    const currentStatus = status?.status ?? 'DISCONNECTED'
    const tone = STATUS_TONES[currentStatus]
    const waiting =
        currentStatus === 'PENDING_QR' || currentStatus === 'CONNECTING'

    const loadStatus = async (id: number) => {
        const resposta = await getWhatsappSessionStatus(id)
        if (resposta.success) {
            setStatus(resposta.result)
            if (resposta.result.qrCode) setQrCode(resposta.result.qrCode)
            if (resposta.result.status === 'CONNECTED') setQrCode(null)
        }
    }

    useEffect(() => {
        if (!isWeb || !departmentId) {
            setStatus(null)
            setQrCode(null)
            return
        }
        void loadStatus(departmentId)
    }, [isWeb, departmentId])

    // Tempo real: QR e transições de estado chegam do worker via Redis Adapter.
    useEffect(() => {
        if (!socket || !isWeb || !departmentId) return

        const qrEvent = `whatsapp-qr-${departmentId}`
        const sessionEvent = `whatsapp-session-${departmentId}`

        const onQr = (dados: { qrCode: string }) => {
            setQrCode(dados.qrCode)
        }
        const onSessionUpdate = (dados: WhatsappChannelStatus) => {
            setStatus(dados)
            if (dados.status === 'CONNECTED') {
                setQrCode(null)
                toast.success('WhatsApp conectado com sucesso')
            }
            if (dados.status === 'LOGGED_OUT') {
                setQrCode(null)
                toast.error('O WhatsApp foi desconectado. Pareie novamente.')
            }
        }

        socket.on(qrEvent, onQr)
        socket.on(sessionEvent, onSessionUpdate)

        return () => {
            socket.off(qrEvent, onQr)
            socket.off(sessionEvent, onSessionUpdate)
        }
    }, [socket, isWeb, departmentId])

    // O QR nasce em segundos. Passando disso, algo está errado no caminho
    // (fila sem consumidor, worker caído) e o operador precisa saber — antes
    // ficava em "Preparando a sessão..." para sempre.
    useEffect(() => {
        if (!requestedAt || qrCode || currentStatus === 'CONNECTED') {
            setNoResponse(false)
            return
        }
        const timer = setTimeout(() => setNoResponse(true), 30_000)
        return () => clearTimeout(timer)
    }, [requestedAt, qrCode, currentStatus])

    // Rede de segurança: se o WebSocket cair no meio do pareamento, o polling
    // ainda traz o QR novo e a confirmação da conexão.
    useEffect(() => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
        }

        if (!isWeb || !departmentId || !waiting) return

        pollingRef.current = setInterval(() => {
            void loadStatus(departmentId)
        }, 5000)

        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current)
        }
    }, [isWeb, departmentId, waiting])

    const connect = async () => {
        if (!departmentId) return
        setCarregando(true)
        setNoResponse(false)
        try {
            const resposta = await connectWhatsappSession(departmentId)
            if (resposta.success) {
                setStatus(resposta.result)
                setRequestedAt(Date.now())
                toast.info('Gerando QR code, aguarde alguns segundos...')
            } else {
                toast.error(resposta.error || 'Erro ao iniciar a conexão')
            }
        } catch {
            toast.error('Erro ao iniciar a conexão')
        } finally {
            setCarregando(false)
        }
    }

    const disconnect = async () => {
        if (!departmentId) return
        setCarregando(true)
        try {
            const resposta = await logoutWhatsappSession(departmentId)
            if (resposta.success) {
                setStatus(resposta.result)
                setQrCode(null)
                toast.success('WhatsApp desconectado')

                // Com o runtime no ar o logout é assíncrono: quem muda o estado
                // é o worker ao processar o job, e a resposta imediata ainda vem
                // "Conectado". Sem esta revalidação o card fica preso nesse
                // estado caso o evento do WebSocket se perca no caminho.
                if (resposta.result?.status === 'CONNECTED') {
                    window.setTimeout(
                        () => void loadStatus(departmentId),
                        2500
                    )
                    window.setTimeout(
                        () => void loadStatus(departmentId),
                        7000
                    )
                }
            } else {
                toast.error(resposta.error || 'Erro ao desconectar')
            }
        } catch {
            toast.error('Erro ao desconectar')
        } finally {
            setCarregando(false)
            setConfirmandoSaida(false)
        }
    }

    return (
        <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-3">
                <div>
                    <p className="text-sm font-medium text-base-content">
                        Canal do WhatsApp
                    </p>
                    <p className="text-xs text-base-content/50">
                        A escolha é gravada na hora, sem esperar o Salvar.
                    </p>
                </div>

                {/* Cartões de escolha, e não dois botões lado a lado: os botões
                    tinham a mesma cara das abas da página logo acima e o
                    operador lia um par de abas dentro de outro par de abas.
                    type="button" continua obrigatório — o bloco vive dentro do
                    <form> do setor. */}
                <div className="grid gap-3 sm:grid-cols-2">
                    {CHANNELS.map(channel => {
                        const selected = provider === channel.kind
                        return (
                            <button
                                key={channel.kind}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => onProviderChange(channel.kind)}
                                disabled={savingChannel}
                                className={`relative flex flex-col gap-2 rounded-xl border p-4 text-left transition-colors disabled:opacity-60 ${
                                    selected
                                        ? 'border-secondary bg-secondary/5 ring-1 ring-secondary/30'
                                        : 'border-base-300 bg-base-100 hover:border-base-content/20 hover:bg-base-200/50'
                                }`}
                            >
                                <span className="absolute right-3 top-3">
                                    {selected ? (
                                        <MdCheckCircle className="text-xl text-secondary" />
                                    ) : (
                                        <span className="block h-5 w-5 rounded-full border-2 border-base-300" />
                                    )}
                                </span>

                                <span
                                    className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                                        selected
                                            ? 'bg-secondary text-secondary-content'
                                            : 'bg-base-200 text-base-content/40'
                                    }`}
                                >
                                    {channel.icon}
                                </span>

                                <span className="flex items-center gap-2 pr-7">
                                    <span className="text-sm font-semibold text-base-content">
                                        {channel.title}
                                    </span>
                                    <span
                                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                                            selected
                                                ? 'bg-secondary/15 text-secondary'
                                                : 'bg-base-200 text-base-content/50'
                                        }`}
                                    >
                                        {channel.tagline}
                                    </span>
                                </span>

                                <span className="text-xs leading-relaxed text-base-content/55">
                                    {channel.description}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </div>

            {!isWeb && departmentId && apiLink && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-base-300 bg-base-200/40 px-4 py-3">
                    <div className="flex min-w-0 items-start gap-3">
                        {apiLink.linked ? (
                            <MdLink className="mt-0.5 shrink-0 text-xl text-success" />
                        ) : (
                            <MdLinkOff className="mt-0.5 shrink-0 text-xl text-warning" />
                        )}
                        <div className="min-w-0">
                            <p className={`text-sm font-medium ${apiLink.linked ? 'text-success' : 'text-warning'}`}>
                                {apiLink.linked ? 'Conectado à conta oficial' : 'Número não vinculado'}
                            </p>
                            <p className="text-xs leading-relaxed text-base-content/60">
                                {apiLink.linked
                                    ? [apiLink.displayPhoneNumber ?? apiLink.whatsappNumber, apiLink.verifiedName].filter(Boolean).join(' · ')
                                    : apiLink.whatsappNumber
                                      ? `O número ${apiLink.whatsappNumber} ainda não foi encontrado na conta do WhatsApp. Sem o vínculo, o setor não envia nem recebe mensagens.`
                                      : 'Informe o número do WhatsApp do setor e salve para vincular.'}
                            </p>
                        </div>
                    </div>
                    {!apiLink.linked && (
                        <button
                            type="button"
                            className="btn btn-sm btn-secondary gap-1"
                            onClick={handleLink}
                            disabled={linking || !apiLink.whatsappNumber}
                        >
                            {linking ? <span className="loading loading-spinner loading-xs" /> : <MdLink />}
                            Vincular
                        </button>
                    )}
                </div>
            )}

            {isWeb && !departmentId && (
                <div className="flex items-start gap-3 rounded-xl border border-info/30 bg-info/5 p-4">
                    <MdQrCode2 className="mt-0.5 shrink-0 text-xl text-info" />
                    <p className="text-sm leading-relaxed text-base-content/70">
                        Salve o setor para liberar o pareamento por QR code.
                    </p>
                </div>
            )}

            {isWeb && departmentId && (
                <div className="overflow-hidden rounded-xl border border-base-300">
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-base-200/50 px-4 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                            <span className="relative flex h-2.5 w-2.5 shrink-0">
                                {tone.pulse && (
                                    <span
                                        className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${tone.dot}`}
                                    />
                                )}
                                <span
                                    className={`relative inline-flex h-2.5 w-2.5 rounded-full ${tone.dot}`}
                                />
                            </span>
                            <div className="min-w-0">
                                <p
                                    className={`text-sm font-semibold ${tone.text}`}
                                >
                                    {STATUS_LABELS[currentStatus]}
                                </p>
                                {status?.phoneNumber && (
                                    <p className="truncate text-xs text-base-content/50">
                                        Número pareado: {status.phoneNumber}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm btn-square"
                                onClick={() => loadStatus(departmentId)}
                                disabled={loading}
                                title="Atualizar status"
                            >
                                <MdRefresh className="text-lg" />
                            </button>

                            {currentStatus === 'CONNECTED' ? (
                                <button
                                    type="button"
                                    className="btn btn-outline btn-error btn-sm gap-1.5"
                                    onClick={() => setConfirmandoSaida(true)}
                                    disabled={loading}
                                >
                                    <MdLinkOff className="text-base" />
                                    Desconectar
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm gap-1.5"
                                    onClick={connect}
                                    disabled={loading || savingChannel}
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdLink className="text-base" />
                                    )}
                                    {currentStatus === 'DISCONNECTED'
                                        ? 'Conectar'
                                        : 'Gerar novo QR code'}
                                </button>
                            )}
                        </div>
                    </div>

                    {(status?.lastError ||
                        (qrCode && currentStatus !== 'CONNECTED') ||
                        waiting ||
                        currentStatus === 'CONNECTED') && (
                        <div className="flex flex-col gap-4 border-t border-base-300 bg-base-100 p-4">
                            {status?.lastError &&
                                currentStatus !== 'CONNECTED' && (
                                    <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/5 p-3">
                                        <MdErrorOutline className="mt-0.5 shrink-0 text-lg text-warning" />
                                        <p className="text-sm leading-relaxed text-base-content/70">
                                            {status.lastError}
                                        </p>
                                    </div>
                                )}

                            {currentStatus === 'CONNECTED' && (
                                <div className="flex items-center gap-3">
                                    <MdCheckCircle className="shrink-0 text-2xl text-success" />
                                    <p className="text-sm leading-relaxed text-base-content/70">
                                        O setor está enviando e recebendo
                                        mensagens por este aparelho. Mantenha o
                                        celular ligado e conectado à internet.
                                    </p>
                                </div>
                            )}

                            {/* QR e instruções lado a lado: empilhados, o passo a
                                passo caía abaixo da dobra em telas menores. */}
                            {qrCode && currentStatus !== 'CONNECTED' && (
                                <div className="grid gap-5 md:grid-cols-[auto_minmax(0,1fr)] md:items-center">
                                    <div className="mx-auto rounded-xl border border-base-300 bg-white p-3 md:mx-0">
                                        <img
                                            src={qrCode}
                                            alt="QR code para parear o WhatsApp"
                                            className="h-48 w-48"
                                        />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-base-content">
                                            Leia o código no celular do setor
                                        </p>
                                        <ol className="mt-3 flex flex-col gap-2.5">
                                            {[
                                                'Abra o WhatsApp no celular do setor',
                                                'Toque em Aparelhos conectados e depois em Conectar um aparelho',
                                                'Aponte a câmera para este código',
                                            ].map((passo, indice) => (
                                                <li
                                                    key={indice}
                                                    className="flex items-start gap-2.5 text-sm leading-relaxed text-base-content/70"
                                                >
                                                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-base-200 text-xs font-semibold text-base-content/60">
                                                        {indice + 1}
                                                    </span>
                                                    {passo}
                                                </li>
                                            ))}
                                        </ol>
                                        <p className="mt-3 text-xs text-base-content/45">
                                            O código expira em cerca de um
                                            minuto. Se expirar, gere um novo.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {waiting && !qrCode && !noResponse && (
                                <div className="flex items-center gap-3 text-sm text-base-content/60">
                                    <span className="loading loading-spinner loading-sm" />
                                    Preparando a sessão...
                                </div>
                            )}

                            {waiting && !qrCode && noResponse && (
                                <div className="flex items-start gap-3 rounded-lg border border-error/30 bg-error/5 p-3">
                                    <MdErrorOutline className="mt-0.5 shrink-0 text-lg text-error" />
                                    <div className="text-sm leading-relaxed">
                                        <p className="font-semibold text-base-content">
                                            O QR code não foi gerado.
                                        </p>
                                        <p className="mt-1 text-base-content/70">
                                            O serviço que mantém as sessões do
                                            WhatsApp não respondeu. Tente
                                            conectar novamente; se persistir,
                                            avise o suporte.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmingDisconnect}
                onClose={() => setConfirmandoSaida(false)}
                onConfirm={disconnect}
                loading={loading}
                title="Desconectar WhatsApp"
                message="O setor deixará de enviar e receber mensagens até que um novo QR code seja lido. Deseja continuar?"
                confirmButtonText="Desconectar"
                cancelButtonText="Cancelar"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
